import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';

import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { SalesAgentService } from '@lmc-app/core/services/sales-agent.service';
import { CreditorService } from '@lmc-app/core/services/creditor.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { FormArray, FormGroup } from '@angular/forms';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import dayjs from 'dayjs';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import { BehaviorSubject, forkJoin, fromEvent, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Loading } from '@lmc-app/applications/loading';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { GoogleAutoAddressCompleteUtils } from '@lmc-app/shared/utils/google-auto-address-complete.utils';
import { ZohoService } from '@lmc-app/core/services/zoho.service';
import { CompletedECService } from '@lmc-app/core/services/completed-ec.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Agency } from '@lmc-app/shared/interface/agency.interface';
import { ModuleFieldPicklist } from '@lmc-app/shared/interface/module-field-picklist';
import { buildDetailsFormConfig } from './details-form.config';
import { DetailsFormOptions } from './details-form-options.interface';
import {
	CollectorSelectorModalComponent,
	CollectorSelectorModalData,
} from '@lmc-app/shared/components/collector-selector-modal/collector-selector-modal.component';
import { Collector } from '@lmc-app/shared/interface/collector.interface';
import { getFieldLabelWithSection, getPhoneFormat } from './details-form-helpers';
import {
	validateAdditionalDebts,
	validateSpeakOnBehalfs,
	validateEmergencyContacts,
	validateCoAppEmergencyContacts,
	validateFormControls,
} from '@lmc-app/shared/formly/validations/application.validation';
import { APPLICATION_FIELD_LABELS } from '@lmc-app/shared/constants/application-field-labels.constant';
import {
	ApplicationUpdatedDialogComponent,
	ApplicationUpdatedDialogData,
	ApplicationUpdatedDialogResult,
} from '../application-updated-dialog/application-updated-dialog.component';
import {
	RemoveCoApplicantDialogComponent,
	RemoveCoApplicantDialogResult,
} from './remove-co-applicant-dialog/remove-co-applicant-dialog.component';
import {
	RestoreCoApplicantDialogComponent,
	RestoreCoApplicantDialogData,
	RestoreCoApplicantDialogResult,
} from './restore-co-applicant-dialog/restore-co-applicant-dialog.component';
import {
	CoApplicantHistoryDialogComponent,
	CoApplicantHistoryDialogData,
} from './co-applicant-history-dialog/co-applicant-history-dialog.component';
import { RemovedCoApplicant } from '@lmc-app/shared/interface/removed-co-applicant.interface';

@Component({
	selector: 'app-details',
	templateUrl: './details.component.html',
	styleUrls: ['./details.component.scss'],
	providers: [
		{
			provide: DateAdapter,
			useClass: MomentDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				appearance: 'outline',      // Sets the border all the way around
				floatLabel: 'always',       // Keeps the label in the floating position
			},
		},
	],
})
export class DetailsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@Input() application: any = null;
	@Output() applicationChange = new EventEmitter<any>();
	@Input() currentUser: User | null = null;
	@Input() selectedTab: string | null = null;
	@Input() saveDate: Date | null = null;
	@Input() cancelDate: Date | null = null;
	@Input() editDate: Date | null = null;
	@Output() saved = new EventEmitter<boolean>();
	@Output() modelChange = new EventEmitter<any>();

	private parentSub = new Subscription();

	// Formly configuration
	protected form = new FormGroup({});
	protected model: any = {};
	protected fields: FormlyFieldConfig[] = [];

	// Dropdown options as Observables
	private leadSources$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private rapidStatuses$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private aobRequireds$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private agencies$ = new BehaviorSubject<Agency[]>([]);
	private salesAgents$ = new BehaviorSubject<User[]>([]);
	private brokers$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private creditors$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private referralMethods$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private agencyLocations$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private creditorPortfolios$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private additionalDebtCreditorPortfolios$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private loanTypes$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private maritalStatuses$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private bestContactTimes$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private noSinReasons$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private applicantTypes$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private industries$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private employmentStatuses$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private employmentStatuses1$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private previousEmploymentType$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private occupations$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private payFrequencies$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private paymentMethods$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private residencyStatuses$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private bankruptcyOptions$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private provinces$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private users$ = new BehaviorSubject<User[]>([]);
	private users: User[];
	protected validationErrors: string[] = [];
	protected lastUpdate: string = "";
	// Override_Type ("name (timestamp)" of the last save) of the loaded application, compared with the server's before saving
	private loadedOverrideType: string = '';
	// This application's RemovedCoApplicantHistory entries not yet restored
	private restorableCoApplicants: RemovedCoApplicant[] = [];
	// All of this application's RemovedCoApplicantHistory entries, newest first
	private coApplicantHistory: RemovedCoApplicant[] = [];
	private coApplicantRemovalSub?: Subscription;
	protected isScrolled = false;
	protected _onDestroy = new Subject<void>();
	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _snackBar = inject(MatSnackBar);
	private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

	constructor(
		private readonly applicationService: ApplicationService,
		private readonly apiService: LegacyApiService,
		private readonly salesAgentService: SalesAgentService,
		private readonly creditorService: CreditorService,
		private readonly zohoService: ZohoService,
		private readonly completedECService: CompletedECService,
		private readonly dialog: MatDialog,
	) { }

	// Card header title/subtitle: props of the formly section-header for the selected tab
	protected get sectionHeader(): { label: string; description?: string } {
		const tab = this.selectedTab === 'details' ? 'details-general' : this.selectedTab;
		const header = this.fields
			.map((group) => group.fieldGroup?.[0])
			.find((field) => field?.type === 'section-header' && field.props?.id === tab);
		return {
			label: header?.props?.label ?? 'Details',
			description: header?.props?.description,
		};
	}

	private _isEditing: boolean = false;

	get isEditing(): boolean {
		return this._isEditing;
	}

	set isEditing(value: boolean) {
		this._isEditing = value;
		this.updateFormState();
	}

	public ngOnInit() {
		this.initializeFormConfig();
		this.isEditing = true;
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes.application?.currentValue) {
			this.application = changes.application?.currentValue;
			if (this.application) {
				this.lastUpdate = this.application.Modified_Time
					? dayjs(this.application.Modified_Time).format('YYYY-MM-DD h:mm:ss A')
					: "";
				this.loadedOverrideType = this.application.Override_Type || '';
				this.loadDropdownOptions();
				this.patchModel();
				this.loadCoApplicantRemoval();
			}
		}

		if (changes.currentUser?.currentValue) {
			this.currentUser = changes.currentUser.currentValue;
			this.model = {
				...this.model,
				Override_Type: this.currentUser?.full_name,
				// Sales Agent stays read-only unless the backend explicitly allows editing
				_canEditSalesAgent: this.currentUser?._canEditSalesAgent === true,
				// Co-applicant actions stay available unless the backend explicitly turns them off
				_canShowCoApplicantHistory: this.currentUser?._coApplicantRemoveRestore?.showHistory !== false,
				_canRemoveCoApplicant: this.currentUser?._coApplicantRemoveRestore?.enableRemove !== false,
				_canRestoreCoApplicant: this.currentUser?._coApplicantRemoveRestore?.enableRestore !== false,
			};
		}

		if (changes.selectedTab?.currentValue) {
			this.model = {
				...this.model,
				_selectedTab: changes.selectedTab?.currentValue,
			}
		}

		if (changes.saveDate?.currentValue) {
			// Saves requested by the summary (Rapid result) run mid-flow, so skip the updated-application prompt
			this.validate(false);
		}

		if (changes.cancelDate?.currentValue) {
			this.discardChanges();
		}

		if (changes.editDate?.currentValue) {
			this.enableEditing();
		}
	}

	public ngAfterViewInit(): void {
		// Show the actions bar shadow only once the summary content has scrolled
		const scrollContainer = this._elementRef.nativeElement.closest('.scrollable-content');
		if (!scrollContainer) {
			return;
		}
		fromEvent(scrollContainer, 'scroll', { passive: true })
			.pipe(
				map(() => scrollContainer.scrollTop > 0),
				distinctUntilChanged(),
				takeUntil(this._onDestroy)
			)
			.subscribe((isScrolled) => (this.isScrolled = isScrolled));
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	protected onModelChange(updatedModel: any) {
		this.modelChange.emit(updatedModel);
	}

	protected onSaveClick($event: MouseEvent): void {
		$event.stopPropagation();
		this.validate();
	}

	protected onCancelClick($event: MouseEvent): void {
		$event.stopPropagation();
		this.discardChanges();
	}

	protected onEditClick($event: MouseEvent): void {
		$event.stopPropagation();
		this.enableEditing();
	}

	private enableEditing(): void {
		this.isEditing = true;
	}

	private validateForm(): boolean {
		this.validationErrors = [];

		// Mark all fields as touched to show validation errors
		this.form.markAllAsTouched();

		// Validate Speak On Behalfs using reusable utility
		const speakOnBehalfs = this.form.get('Speak_on_Behalf');
		if (speakOnBehalfs instanceof FormArray) {
			validateSpeakOnBehalfs(speakOnBehalfs, this.validationErrors);
		}

		// Validate Emergency Contacts using reusable utility
		const emergencyContacts = this.form.get('EMERGANCY_CONTACTS');
		if (emergencyContacts instanceof FormArray) {
			validateEmergencyContacts(emergencyContacts, this.validationErrors);
		}

		// Validate Co-appliant Emergency Contacts using reusable utility
		const coAppEmergencyContacts = this.form.get('Co_Applicant_EC');
		if (coAppEmergencyContacts instanceof FormArray) {
			validateCoAppEmergencyContacts(coAppEmergencyContacts, this.validationErrors);
		}

		// Validate Additional Debts using reusable utility
		const additionalDebts = this.form.get('Additional_Debt');
		if (additionalDebts instanceof FormArray) {
			validateAdditionalDebts(additionalDebts, this.validationErrors);
		}

		// Validate regular form controls using reusable utility
		if (this.form.invalid) {
			validateFormControls(
				this.form,
				this.validationErrors,
				(fields, key) => getFieldLabelWithSection(this.fields, key)
			);

			return false;
		}

		return true;
	}

	private validate(checkForUpdates: boolean = true): void {
		// Validate form before saving
		if (!this.validateForm()) {
			// Scroll to top to show validation errors
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}

		// Clear validation errors if form is valid
		this.validationErrors = [];

		if (checkForUpdates) {
			this.checkForUpdatesThenSave();
		} else {
			this.saveChanges();
		}
	}

	/**
	 * Re-fetch the application before saving; if someone else saved it after it was loaded
	 * (its Override_Type stamp changed), let the user reload the latest data
	 */
	private checkForUpdatesThenSave(): void {
		// The warning is switched on per user by _staleDataNotification.showStaleOnSave
		if (!this.currentUser?._staleDataNotification?.showStaleOnSave) {
			this.saveChanges();
			return;
		}

		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Checking for updates' },
		});

		this.parentSub.add(this.applicationService.getApplication(this.application.id).subscribe({
			next: (latestApplication) => {
				if ((latestApplication?.Override_Type || '') === this.loadedOverrideType) {
					this.saveChanges();
					return;
				}

				this._bottomSheet.dismiss();
				const dialogRef = this.dialog.open<ApplicationUpdatedDialogComponent, ApplicationUpdatedDialogData, ApplicationUpdatedDialogResult>(
					ApplicationUpdatedDialogComponent,
					{
						width: '480px',
						maxWidth: '90vw',
						disableClose: true,
						data: {
							modifiedTime: dayjs(latestApplication?.Modified_Time).format('YYYY-MM-DD h:mm A'),
							lastDetailsSave: latestApplication?.Override_Type || '',
						},
					}
				);

				this.parentSub.add(dialogRef.afterClosed().subscribe((result) => {
					if (result === 'reload') {
						// The summary takes the latest application and passes it back in, which resets the form
						this.applicationChange.emit(latestApplication);
					}
				}));
			},
			error: (error) => {
				// Do not block saving when the check itself fails
				console.error(error);
				this.saveChanges();
			},
		}));
	}

	private saveChanges(): void {
		const formValues = { ...this.model, Override_Type: this.getOverrideType(), };

		// Convert moment dates to string format
		Object.keys(formValues).forEach((key) => {
			if (formValues[key]?._isAMomentObject) {
				formValues[key] = formValues[key].format('YYYY-MM-DD');
			}
		});

		if(formValues.Owner && !this.users.map(user => user.zoho_owner_id).includes(formValues.Owner)) {
			formValues.Owner = "2390707000063682025";
		}

		if(formValues.Owner1 && !this.users.map(user => user.zoho_owner_id).includes(formValues.Owner1)) {
			formValues.Owner1 = "2390707000063682025";
		}

		// Compute Length_of_Employment from Years + Months
		if (formValues._employmentYears !== undefined || formValues._employmentMonths !== undefined) {
			formValues.Length_of_Employment = (
				(Number(formValues._employmentYears) || 0) * 12 +
				(Number(formValues._employmentMonths) || 0)
			).toString();
		}

		// Compute Secondary_Years_Worked from Years + Months
		if (formValues._coApplicantEmploymentYears !== undefined || formValues._coApplicantEmploymentMonths !== undefined) {
			formValues.Secondary_Years_Worked = (
				(Number(formValues._coApplicantEmploymentYears) || 0) * 12 +
				(Number(formValues._coApplicantEmploymentMonths) || 0)
			).toString();
		}

		// Clean up local-only keys (prefixed with _)
		Object.keys(formValues)
			.filter((k) => k.startsWith('_'))
			.forEach((k) => delete formValues[k]);

		// Set Emer_Cont based on the EMERGANCY_CONTACTS rows that will remain after
		// the save. Existing rows are removed by flagging them with _delete (see
		// RepeatingECSectionType), so they must not be counted here.
		const remainingECs = (formValues.EMERGANCY_CONTACTS || []).filter(
			(form_ec: { _delete?: boolean }) => !form_ec._delete
		);
		formValues.Emer_Cont = remainingECs.length > 0 ? 'Yes' : 'No';

		if (formValues.EMERGANCY_CONTACTS) {
			const savedECs = formValues.EMERGANCY_CONTACTS.filter((form_ec: { id: string }) => form_ec.id);
			// The completed status is saved as soon as it is confirmed (see toggleECComplete); only removed ECs are left to clean up
			savedECs.filter((form_ec: { _delete: boolean }) => form_ec._delete)
				.forEach((form_ec: { id: string }) => {
					this.removeCompletedEC(form_ec);
				});

			// Clean up local-only keys (prefixed with _)
			Object.keys(formValues.EMERGANCY_CONTACTS)
				.filter((k) => k.startsWith('_'))
				.forEach((k) => delete formValues.EMERGANCY_CONTACTS[k]);
		}

		if (formValues.Co_Applicant_EC) {
			const savedECs = formValues.Co_Applicant_EC.filter((form_ec: { id: string }) => form_ec.id);
			// The completed status is saved as soon as it is confirmed (see toggleECComplete); only removed ECs are left to clean up
			savedECs.filter((form_ec: { _delete: boolean }) => form_ec._delete)
				.forEach((form_ec: { id: string }) => {
					this.removeCompletedEC(form_ec);
				});

			// Clean up local-only keys (prefixed with _)
			Object.keys(formValues.Co_Applicant_EC)
				.filter((k) => k.startsWith('_'))
				.forEach((k) => delete formValues.Co_Applicant_EC[k]);
		}

		// Set Additional_Debt2 based on the Additional_Debt rows that will remain
		// after the save. Existing rows are removed by flagging them with _delete
		// (see RepeatingSectionType), so they must not be counted here.
		const remainingAdditionalDebt = (formValues.Additional_Debt || []).filter(
			(debt: { _delete?: boolean }) => !debt._delete
		);
		formValues.Additional_Debt2 = remainingAdditionalDebt.length > 0 ? 'Yes' : 'No';

		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Saving application' },
		});

		this.parentSub.add(this.applicationService
			.updateApplication(this.application.id, formValues)
			.subscribe({
				next: (updatedApplication) => {
					// Our own save is not an outside update: compare the next save against its stamp
					this.loadedOverrideType = updatedApplication?.Override_Type || formValues.Override_Type;
					// The co-applicant now exists only once its first name has been saved
					// _hasSavedCoApplicant for remove Co-Applicant action
					this.model = {
						...this.model,
						_hasSavedCoApplicant: !!formValues.Co_Applicant_Name?.trim(),
					};
					this.saved.emit();
					this.applicationChange.emit(this.application);
					this._bottomSheet.dismiss();
					this._snackBar.open(
						'Application update successful',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					this.parentSub.add(this.creditorService.getCreditors().subscribe((creditors) => {
						const addedCreditors: string[] = [];
						if (formValues.Creditt && !creditors.some((creditor: { display_value: string }) => formValues.Creditt === creditor["display_value"])) {
							if (!addedCreditors.includes(formValues.Creditt)) {
								addedCreditors.push(formValues.Creditt);
								this.parentSub.add(this.creditorService.createCreditor({ name: formValues.Creditt, employeeID: this.currentUser?.employeeID }).subscribe({}));
							}
						}
						formValues.Additional_Debt.forEach((debt: { misc: string }) => {
							if (debt.misc && !creditors.some((creditor: { display_value: string }) => debt.misc === creditor["display_value"])) {
								if (!addedCreditors.includes(debt.misc)) {
									addedCreditors.push(debt.misc);
									this.parentSub.add(this.creditorService.createCreditor({ name: debt.misc, employeeID: this.currentUser?.employeeID }).subscribe());
								}
							}
						});
					}));
					//this.isEditing = false;
				},
				error: (error) => {
					console.error(error);
					this._bottomSheet.dismiss();

					// Parse Zoho error response
					const errorMessage = this.parseZohoError(error);

					this._snackBar.open(
						errorMessage,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_LONG
					);
				},
			}));
	}

	private saveECCompletedNote(ec: { Name1: string, Relation_to_Applicant: string, Phone_Number: string }): void {
		const noteInfo = {
			subject: "EC Completed",
			description: "Emp. confirmed - pass probation, length of employment.",
			communication: "INT",
			RLI_Called_Count: "",
			email_address: "",
			email_notes: "",
			note: `Name: ${ec.Name1}, Relation: ${ec.Relation_to_Applicant}, Phone: ${getPhoneFormat(ec.Phone_Number)}`,
		};
		this.parentSub.add(this.apiService
			.createNoteLocalDB(
				noteInfo,
				this.application.Deal_Name,
				this.application.id,
				this.currentUser?.full_name,
				this.currentUser?.zoho_owner_id,
				false,
				'',
			)
			.subscribe((res) => {
				if (res.message == 'Success') {
					let app_note_id = res['data'][0]['app_note_id'] ?? '';
					// Push to ZOHO
					this.parentSub.add(this.apiService
						.addLoanInterviewNotesZOHO(
							noteInfo,
							this.application.id,
							this.currentUser?.zoho_owner_id
						)
						.subscribe((res) => {
							if (res['message']['statusCode'] === 201) {
								let zoho_note_id = res['app_id'];
								this.parentSub.add(this.apiService
									.updateNoteIDLocal(app_note_id, zoho_note_id)
									.subscribe(() => { }));

								this._snackBar.open('Note saved successfully!', 'Close', {
									duration: 3000,
									panelClass: ['success-snackbar'],
								});
							} else {
								this.parentSub.add(this.apiService
									.emailNotesToIT(
										noteInfo,
										this.application.id,
										this.application.Deal_Name,
										this.currentUser?.full_name
									)
									.subscribe((res) => {
										console.log(res);
									}));
								console.error(res, 'ZOHO Note push failed!');
								this._snackBar.open('Failed to save note to ZOHO', 'Close', {
									duration: 5000,
									panelClass: ['error-snackbar'],
								});
							}
						}));
				} else {
					this._snackBar.open('Failed to save note!', 'Close', {
						duration: 5000,
						panelClass: ['error-snackbar'],
					});
					console.error(res, 'Platform: ZOHO Note save failed!');
				}
			}));
	}

	/**
	 * Saves the completed status of one emergency contact on its own, leaving the rest of the form unsaved.
	 * Emits whether it was saved.
	 */
	private toggleECComplete(
		ec: { id: string, Name1: string, Relation_to_Applicant: string, Phone_Number: string },
		complete: boolean,
		listKey: 'EMERGANCY_CONTACTS' | 'Co_Applicant_EC',
	): Observable<boolean> {
		return this.completedECService.setCompletedEC(ec.id, complete).pipe(
			map(() => {
				// Keep the loaded application in step, so discarding changes does not bring back the old status
				const loadedEC = this.application?.[listKey]?.find((e: { id: string }) => e.id === ec.id);
				if (loadedEC) {
					loadedEC._completed = complete;
				}
				if (complete) {
					this.saveECCompletedNote(ec);
				}
				this._snackBar.open(
					complete ? 'Emergency contact marked as completed' : 'Emergency contact completed status removed',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				return true;
			}),
			catchError((error) => {
				console.error(error);
				this._snackBar.open(
					'Failed to update the emergency contact completed status',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_LONG
				);
				return of(false);
			}),
		);
	}

	private removeCompletedEC(ec: { id: string, }): void {
		this.parentSub.add(this.completedECService.deleteCompletedEC(ec.id).subscribe(() => { }));
	}

	private discardChanges(): void {
		// Restore form to original application data
		this.patchModel();
		this.isEditing = false;
	}

	private getLeadSources() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Lead_Source').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.leadSources$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getRapidStatuses() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Opportunity_Type').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.rapidStatuses$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getAgencies() {
		this.parentSub.add(this.zohoService.getAgencies().subscribe({
			next: (res) => {
				this.agencies$.next(res);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getSalesAgents() {
		this.parentSub.add(this.salesAgentService.getSalesAgents().subscribe({
			next: (res) => {
				this.salesAgents$.next(res);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getBrokers() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Broker').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.brokers$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getCreditors() {
		this.parentSub.add(this.creditorService.getCreditors().subscribe({
			next: (res) => {
				this.creditors$.next(res);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getReferralMethods() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Referral_type').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.referralMethods$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getAgencyLocations() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Agency_Location').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.agencyLocations$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getCreditorPortfolio() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Creditor_Portfolio').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.creditorPortfolios$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getAdditionalDebtCreditorPortfolio() {
		this.parentSub.add(this.apiService.getModuleField('Additional_Debt', 'Creditor_Portfolio').subscribe({
			next: (res) => {
				const filtered = [{ display_value: '', reference_value: '', actual_value: '' }, ...res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				)];
				this.additionalDebtCreditorPortfolios$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getLoanTypes() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Loan_Type').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.loanTypes$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getPayFrequencies() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Pay_Frequency2').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.payFrequencies$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getMaritalStatus() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Marital_Status').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.maritalStatuses$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getBestContactTimes() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Best_contact_time').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.bestContactTimes$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getNoSinReasons() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'No_SIN_Reason').subscribe({
			next: (res: { pick_list_values: ModuleFieldPicklist[] }) => {
				this.noSinReasons$.next(res.pick_list_values);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getApplicantTypes() {
		this.parentSub.add(this.apiService.getModuleField('Speak_on_Behalf', 'Applicant_Type').subscribe({
			next: (res) => {
				const values = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.applicantTypes$.next(values);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getIndustries() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Industry').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.industries$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getEmploymentStatuses() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Employment_Status').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.employmentStatuses$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getEmploymentStatuses1() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Employment_Status1').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.employmentStatuses1$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getPreviousEmploymentType() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Previous_Employment_Type').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.previousEmploymentType$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getOccupations() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Occupation').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.occupations$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getPaymentMethods() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Payment_Method').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.paymentMethods$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getResidencyStatuses() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Residency_Status').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.residencyStatuses$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getBankruptcyOptions() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Bankruptcy_CC_CP').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.bankruptcyOptions$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getProvinces() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Province1').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.provinces$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getUsers() {
		this.parentSub.add(this.apiService.getUsers().subscribe({
			next: (res) => {
				this.users = JSON.parse(JSON.stringify(res)); // deep clone of users, as users$ maybe modified by searchable-select
				this.users$.next(res);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private initializeFormConfig() {
		const options: DetailsFormOptions = {
			leadSources$: this.leadSources$,
			rapidStatuses$: this.rapidStatuses$,
			aobRequireds$: this.aobRequireds$,
			salesAgents$: this.salesAgents$,
			brokers$: this.brokers$,
			creditors$: this.creditors$,
			referralMethods$: this.referralMethods$,
			agencyLocations$: this.agencyLocations$,
			creditorPortfolios$: this.creditorPortfolios$,
			additionalDebtCreditorPortfolios$: this.additionalDebtCreditorPortfolios$,
			loanTypes$: this.loanTypes$,
			maritalStatuses$: this.maritalStatuses$,
			bestContactTimes$: this.bestContactTimes$,
			noSinReasons$: this.noSinReasons$,
			applicantTypes$: this.applicantTypes$,
			industries$: this.industries$,
			employmentStatuses$: this.employmentStatuses$,
			employmentStatuses1$: this.employmentStatuses1$,
			previousEmploymentType$: this.previousEmploymentType$,
			occupations$: this.occupations$,
			payFrequencies$: this.payFrequencies$,
			paymentMethods$: this.paymentMethods$,
			residencyStatuses$: this.residencyStatuses$,
			bankruptcyOptions$: this.bankruptcyOptions$,
			provinces$: this.provinces$,
			users$: this.users$,
			agencies$: this.agencies$,
			onAddressSelect: (result: any, applicant: string) =>
				this.handleAddressSelect(result, applicant),
			getAgencyName: (agencyId: string) => this.getAgencyNameById(agencyId),
			onCollectorButtonClick: (
				agency: Agency,
				selectedCollector: Collector | undefined,
				field: FormlyFieldConfig
			) => this.handleCollectorButtonClick(agency, selectedCollector, field),
			onToggleECComplete: (ec, complete, listKey) =>
				this.toggleECComplete(ec, complete, listKey as 'EMERGANCY_CONTACTS' | 'Co_Applicant_EC'),
			onRemoveCoApplicantClick: () => this.handleRemoveCoApplicantClick(),
			onRestoreCoApplicantClick: () => this.handleRestoreCoApplicantClick(),
			onCoApplicantHistoryClick: () => this.handleCoApplicantHistoryClick(),
		};
		this.fields = buildDetailsFormConfig(options);
	}

	private updateFormState() {
		if (this._isEditing) {
			this.form.enable();
			this.form.markAllAsTouched();
			// Update field props without recreating the entire fields array
			// This preserves the expansion panel state
			this.updateFieldsDisabledState(this.fields, false);
		} else {
			this.form.disable();
			// Update field props without recreating the entire fields array
			// This preserves the expansion panel state
			this.updateFieldsDisabledState(this.fields, true);
		}
	}

	/**
	 * Recursively update the disabled state of fields without recreating the array
	 * This preserves the expansion panel state during view/edit mode transitions
	 */
	private updateFieldsDisabledState(
		fields: FormlyFieldConfig[],
		disabled: boolean
	) {
		fields.forEach((field) => {
			if (field.props) {
				field.props.disabled = disabled;
			}
			if (field.fieldGroup) {
				this.updateFieldsDisabledState(field.fieldGroup, disabled);
			}
			if (field.fieldArray) {
				// fieldArray can be a function or FormlyFieldConfig
				const fieldArray =
					typeof field.fieldArray === 'function'
						? field.fieldArray(field)
						: field.fieldArray;
				if (fieldArray.fieldGroup) {
					this.updateFieldsDisabledState(fieldArray.fieldGroup, disabled);
				}
			}
		});
	}

	private loadDropdownOptions() {
		this.getLeadSources();
		this.getRapidStatuses();
		this.getAgencies();
		this.getSalesAgents();
		this.getBrokers();
		this.getCreditors();
		this.getReferralMethods();
		this.getAgencyLocations();
		this.getCreditorPortfolio();
		this.getAdditionalDebtCreditorPortfolio();
		this.getLoanTypes();
		this.getMaritalStatus();
		this.getBestContactTimes();
		this.getNoSinReasons();
		this.getApplicantTypes();
		this.getIndustries();
		this.getEmploymentStatuses();
		this.getEmploymentStatuses1();
		this.getPreviousEmploymentType();
		this.getOccupations();
		this.getPayFrequencies();
		this.getPaymentMethods();
		this.getResidencyStatuses();
		this.getBankruptcyOptions();
		this.getProvinces();
		this.getUsers();
	}

	private getAgencyNameById(agencyId: string): string {
		if (!agencyId) return 'No Agency Selected';
		const agencies = this.agencies$.getValue();
		const agency = agencies.find((a) => a.id === agencyId);
		return agency?.Account_Name || 'Selected Agency';
	}

	// Potential associated data related to Co-Applicant 
	private handleCollectorButtonClick(
		agency: Agency,
		selectedCollector: Collector | undefined,
		field: FormlyFieldConfig
	) {
		const dialogData: CollectorSelectorModalData = {
			agency,
			selectedCollector,
		};

		const dialogRef = this.dialog.open(CollectorSelectorModalComponent, {
			width: '80vw',
			height: '80vh',
			maxWidth: '80vw',
			maxHeight: '80vh',
			data: dialogData,
			disableClose: false,
		});

		this.parentSub.add(dialogRef.afterClosed().subscribe((collector: Collector | null) => {
			if (collector) {
				field.formControl?.setValue(collector);
				field.formControl?.markAsTouched();

				if (field.props?.change) {
					field.props.change(field);
				}
			}
		}));
	}

	private handleAddressSelect(result: any, applicant: string) {
		const parsedAddress =
			GoogleAutoAddressCompleteUtils.parseAddressComponents(result);

		if (applicant === 'MAIN') {
			this.model = {
				...this.model,
				Address1: parsedAddress.address1,
				City: parsedAddress.city,
				Province: parsedAddress.province,
				Postal_Code: parsedAddress.postalCode,
			};
		} else if (applicant === 'CO') {
			this.model = {
				...this.model,
				Co_Applicant_Property_Address: parsedAddress.address1,
				Co_Applicant_City: parsedAddress.city,
				Co_Applicant_Province: parsedAddress.province,
				Co_Applicant_Postal_Code: parsedAddress.postalCode,
			};
		}
	}

	private patchModel() {
		const today = new Date();
		this.model = {
			_selectedTab: this.selectedTab,
			_stage: this.application.Stage,
			// Sales Agent stays read-only unless the backend explicitly allows editing
			_canEditSalesAgent: this.currentUser?._canEditSalesAgent === true,
			// Co-applicant actions stay available unless the backend explicitly turns them off
			_canShowCoApplicantHistory: this.currentUser?._coApplicantRemoveRestore?.showHistory !== false,
			_canRemoveCoApplicant: this.currentUser?._coApplicantRemoveRestore?.enableRemove !== false,
			_canRestoreCoApplicant: this.currentUser?._coApplicantRemoveRestore?.enableRestore !== false,
			// Whether the saved application already has a sales agent; without one, anyone may assign it
			_hasSavedSalesAgent: !!this.application.Sales_Agent_ID,
			// Whether the saved application already has a co-applicant (first name saved)
			_hasSavedCoApplicant: !!this.application.Co_Applicant_Name?.trim(),
			// Whether a removed co-applicant can be restored (loaded from RemovedCoApplicantHistory)
			_hasRemovedCoApplicant: this.restorableCoApplicants.length > 0,
			// Whether the application has any co-applicant removals or restores to show
			_hasCoApplicantHistory: this.coApplicantHistory.length > 0,

			// GENERAL section
			Lead_Source: this.application.Lead_Source,
			Deal_Name: this.application.Deal_Name,
			Opportunity_Type: this.application.Opportunity_Type,
			Owner: this.application.Owner?.id || null,
			Owner1: this.application.Owner1?.id || null,
			App_Number: this.application.App_Number,
			_Application_Number: this.application.Application_Number,
			AOB_file: this.application.AOB_file,
			AOB_required: this.application.AOB_required,
			AOB_full_name: this.application.AOB_full_name,
			_Refinanced_Amount: this.application.Refinanced_Amount,
			_Amount: this.application.Amount,
			_Approved_Amount: this.application.Approved_Amount,
			_Banking_method: this.application.Banking_method,
			_FinalAmount: this.application.FinalAmount,
			_Final_Agency_Amount: this.application.Final_Agency_Amount,
			_API_Created_By: this.application.API_Created_By?.id || "N/A",
			_API_Modified_By: this.application.API_Modified_By.length ? this.application.API_Modified_By.map((user: { id: any }) => user.id)[0] : "N/A",
			Override_Type: this.currentUser?.full_name,
			_Created_By: this.application.Created_By?.name || "N/A",
			_Modified_By: this.application.Modified_By?.id || "N/A",
			// PREQUALIFYING section
			Residency_Status: this.application.Residency_Status,
			Employment_Status: this.application.Employment_Status,
			Do_you_earn_at_least_1200_per_month:
				this.application.Do_you_earn_at_least_1200_per_month,
			Payday_loans: this.application.Payday_loans,
			Employed: this.application.Employed,
			Are_you_currently_a_Home_Owner:
				this.application.Are_you_currently_a_Home_Owner,
			Province1: this.application.Province1,
			MTG_Balance: this.application.MTG_Balance,
			Estimated_Value: this.application.Estimated_Value,
			Loan_Det: this.application.Loan_Det,
			Mortgage_Details: this.application.Mortgage_Details,
			How_much_do_you_currently_owe_the_CRA:
				this.application.How_much_do_you_currently_owe_the_CRA,
			If_needed_could_you_find_a_Co_Singer:
				this.application.If_needed_could_you_find_a_Co_Singer,
			native_reserve: this.application.native_reserve,
			Bankruptcy_CC_CP: this.application.Bankruptcy_CC_CP,
			Consent_Form_Signed: this.application.Consent_Form_Signed,
			Payment_Method: this.application.Payment_Method,
			Is_this_a_staffing_agency: this.application.Is_this_a_staffing_agency,

			// REFERRAL section
			Account_Name: this.application.Account_Name?.id || null,
			Sales_Agent_ID: this.application.Sales_Agent_ID,
			Sales_Agent: this.application.Sales_Agent,
			Original_Sales_Agent: this.application.Sales_Agent,
			Referred_By: this.application.Referred_By,
			Broker: this.application.Broker,
			Creditt: this.application.Creditt,
			Referral_type: this.application.Referral_type,
			Creditor_Agency_Name: this.application.Creditor_Agency_Name,
			Referral_Date: this.application.Referral_Date || today.toISOString().split('T')[0],
			Acc_Number: this.application.Acc_Number,
			Closing_Date: this.application.Closing_Date || new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0],
			Account_Bal: this.application.Account_Bal,
			Total_Legal_Fees: this.application.Total_Legal_Fees,
			Legal_Fee: this.application.Legal_Fee,
			Agency_Location: this.application.Agency_Location,
			Settlement_Amount: this.application.Settlement_Amount,
			Creditor_Portfolio: this.application.Creditor_Portfolio,
			Additional_Debt2: this.application.Additional_Debt2,
			Loan_Type: this.application.Loan_Type,
			Custom_Settlement: this.application.Custom_Settlement,
			Loyalty_Payment_Plan: this.application.Loyalty_Payment_Plan || "No",
			Custom_Agency_Amount: this.application.Custom_Agency_Amount,
			Additional_Debt:
				this.application.Additional_Debt?.map((debt: any) => ({
					id: debt.id,
					Type_of_Credit: debt.Type_of_Credit,
					Agency: debt.Agency?.id,
					Collector: debt.Collector,
					misc: debt.misc,
					Creditor_Portfolio: debt.Creditor_Portfolio,
					Custom_Settlement: debt.Custom_Settlement,
					Account_Balance: debt.Account_Balance,
					Account_Number: debt.Account_Number,
					Custom_Agency_Amount: debt.Custom_Agency_Amount,
					Legal_Fee: debt.Legal_Fee,
					Settlement_Amount: debt.Settlement_Amount,
				})) || [],

			// CLIENT section - Personal
			contactId: this.application.Contact_Name?.id,
			First_Name: this.application.Contact_Name?.First_Name,
			Middle_Name: this.application.Contact_Name?.Middle_Name,
			Last_Name: this.application.Contact_Name?.Last_Name,
			Gender: this.application.Gender,
			Do_you_have_another_name: this.application.Do_you_have_another_name,
			Number_of_Dependents: this.application.Number_of_Dependents,
			Alternative_Name: this.application.Alternative_Name,
			Date_of_Birth: this.application.Date_of_Birth,
			best_voe_contact_time_range: this.application.best_voe_contact_time_range,
			Marital_Status: this.application.Marital_Status,
			best_contact_time_range: this.application.best_contact_time_range,
			S_I_N: this.application.S_I_N,
			Last_Contact_Date: this.application.Last_Contact_Date,
			Email1: this.application.Email1,
			Does_you_own_a_car: this.application.Does_you_own_a_car,
			Mobile: this.application.Mobile,
			What_is_the_value_of_the_car:
				this.application.What_is_the_value_of_the_car,
			Phone: this.application.Phone,
			No_SIN_Reason: this.application.No_SIN_Reason,
			Co_Appl_Required: this.application.Co_Appl_Required,
			Co_Applicant_LPP: this.application.Co_Applicant_LPP ?? 'No',

			// CLIENT section - Address
			Address1: this.application.Address1,
			Length_of_Residence: this.application.Length_of_Residence,
			Address_Line_2: this.application.Address_Line_2,
			Monthly_Payments: this.application.Monthly_Payments,
			City: this.application.City,
			Province: this.application.Province,
			Postal_Code: this.application.Postal_Code,

			// CLIENT section - Speak on behalf
			Speak_on_Behalf: this.application.Speak_on_Behalf || [],

			// CLIENT section - Banking
			Financial_Institution_Name: this.application.Financial_Institution_Name,
			Financial_Institution_Number:
				this.application.Financial_Institution_Number,
			Bank_Account_Number: this.application.Bank_Account_Number,
			Bank_Transit_No: this.application.Bank_Transit_No,
			Branch_Address: this.application.Branch_Address,

			// CLIENT section - Employment
			Employer: this.application.Employer,
			Industry: this.application.Industry,
			Employment_Status1: this.application.Employment_Status1,
			Occupation: this.application.Occupation,
			Work_Phone: this.application.Work_Phone,
			Pay_Frequency2: this.application.Pay_Frequency2,
			Work_Extension: this.application.Work_Extension,
			Income_net_paycheque_income: this.application.Income_net_paycheque_income,
			_employmentYears: Math.floor((Number(this.application.Length_of_Employment) || 0) / 12),
			_employmentMonths: (Number(this.application.Length_of_Employment) || 0) % 12,
			Last_Pay_Date: this.application.Last_Pay_Date,
			Position: this.application.Position,
			Supervisor_Name: this.application.Supervisor_Name,
			Previous_Employment:
				this.application.Previous_Employment === true ? 'Yes' : 'No',
			Next_Pay_Date: this.application.Next_Pay_Date,
			Supervisor_Phone_Number: this.application.Supervisor_Phone_Number,
			Additional_Employment: this.application.Additional_Employment,

			// CLIENT section - Previous Employment
			Previous_Employment_Type: this.application.Previous_Employment_Type,
			Previous_Position: this.application.Previous_Position,
			Previous_Employer: this.application.Previous_Employer,
			Previous_Length_of_Employment:
				this.application.Previous_Length_of_Employment,
			Previous_Supervisor_Name: this.application.Previous_Supervisor_Name,
			Previous_Supervisor_Phone_number:
				this.application.Previous_Supervisor_Phone_number,
			Previous_Income_net_paycheque_income:
				this.application.Previous_Income_net_paycheque_income,
			Previous_Pay_Frequency: this.application.Previous_Pay_Frequency,
			Previous_Payment_Method: this.application.Previous_Payment_Method,

			// CLIENT EC section - Emergency Contacts
			EMERGANCY_CONTACTS: this.application.EMERGANCY_CONTACTS?.map((ec: { _completed: boolean }) => ({
				...ec,
				_complete: ec._completed,
			})) || [],

			// CO-APPLICANT section - Personal
			Co_Applicant_Name: this.application.Co_Applicant_Name,
			Co_Applicant_Middle_Name: this.application.Co_Applicant_Middle_Name,
			Guarantor_Name: this.application.Guarantor_Name,
			Co_Applicant_Gender: this.application.Co_Applicant_Gender,
			Co_Applicant_Date_of_Birth: this.application.Co_Applicant_Date_of_Birth,
			Secondary_S_I_N: this.application.Secondary_S_I_N,
			Email: this.application.Email,
			Secondary_Cell_Number: this.application.Secondary_Cell_Number,
			Secondary_Home_Number: this.application.Secondary_Home_Number,

			// CO-APPLICANT section - Address
			Co_Applicant_Property_Address:
				this.application.Co_Applicant_Property_Address,
			Co_Applicant_Address_Line_2_Unit_number:
				this.application.Co_Applicant_Address_Line_2_Unit_number,
			Co_Applicant_City: this.application.Co_Applicant_City,
			Co_Applicant_Province: this.application.Co_Applicant_Province,
			Co_Applicant_Postal_Code: this.application.Co_Applicant_Postal_Code,

			// CO-APPLICANT section - Employment
			Co_App_Residency_Status: this.application.Co_App_Residency_Status,
			Co_App_Employment_Type: this.application.Co_App_Employment_Type,
			Co_App_BK_CC_CP: this.application.Co_App_BK_CC_CP,
			Co_Applicant_Employer: this.application.Co_Applicant_Employer,
			Co_Applicant_Status: this.application.Co_Applicant_Status,
			Secondary_Position: this.application.Secondary_Position,
			_coApplicantEmploymentYears: Math.floor((Number(this.application.Secondary_Years_Worked) || 0) / 12),
			_coApplicantEmploymentMonths: (Number(this.application.Secondary_Years_Worked) || 0) % 12,
			Secondary_Work_Number: this.application.Secondary_Work_Number,
			Sec_Annual_Income: this.application.Sec_Annual_Income,
			Co_Applicant_Pay_Frequency: this.application.Co_Applicant_Pay_Frequency,

			// CO-APPLICANT EC section - Emergency Contacts
			Co_Applicant_EC: this.application.Co_Applicant_EC?.map((ec: { _completed: boolean }) => ({
				...ec,
				_complete: ec._completed,
			})) || [],

			// CO-APPLICANT section - Banking
			Co_Applicant_Financial_Institution_Name:
				this.application.Co_Applicant_Financial_Institution_Name,
			Co_ApplicantFinancial_Institution_Number1:
				this.application.Co_ApplicantFinancial_Institution_Number1,
			Co_Applicant_Bank_Account: this.application.Co_Applicant_Bank_Account,
			Co_Applicant_Bank_Transit_No:
				this.application.Co_Applicant_Bank_Transit_No,
			Co_Applicant_Branch_Address: this.application.Co_Applicant_Branch_Address,
		};
	}

	/**
	 * Parses Zoho error response and converts it to user-friendly error message
	 * @param error - The error object from HTTP response
	 * @returns Formatted error message string
	 */
	private parseZohoError(error: any): string {
		try {
			// error.error.data is the Zoho error array
			const zohoErrors = error?.error?.data || [];
			const errorMessages: string[] = [];

			zohoErrors.forEach((zohoError: any) => {
				let jsonPath = zohoError.details?.json_path;

				if (jsonPath) {
					const parts = jsonPath.split('.');
					jsonPath = parts.length > 2 ? parts[2] : null;
				}

				const message = zohoError.message;

				// Map API field name to user-friendly label
				const fieldLabel = APPLICATION_FIELD_LABELS[jsonPath] || 'Unknown field';
				errorMessages.push(`[Zoho Error] ${fieldLabel}: ${message}`);
			});

			return errorMessages.length > 0
				? errorMessages.join('; ')
				: 'Application update failure';
		} catch (err) {
			console.error('Error parsing Zoho error response:', err);
			return 'Application update failure';
		}
	}

	// ---------------------------------------------------------------------------
	// Remove co-applicant
	// ---------------------------------------------------------------------------

	// A removal not yet restored swaps the co-applicant action from Remove to Restore;
	// any history at all adds the History action next to them
	private loadCoApplicantRemoval() {
		const applicationId = this.application.id;
		this.coApplicantRemovalSub?.unsubscribe();
		this.coApplicantRemovalSub = forkJoin({
			removals: this.applicationService.getRestorableCoApplicants(applicationId),
			history: this.applicationService.getCoApplicantHistory(applicationId),
		})
			.subscribe({
				next: ({ removals, history }) => {
					this.restorableCoApplicants = removals;
					this.coApplicantHistory = history;
					this.model = {
						...this.model,
						_hasRemovedCoApplicant: removals.length > 0,
						_hasCoApplicantHistory: history.length > 0,
					};
				},
				error: (error) => console.error(error),
			});
		this.parentSub.add(this.coApplicantRemovalSub);
	}

	private handleRemoveCoApplicantClick() {
		const dialogRef = this.dialog.open<RemoveCoApplicantDialogComponent, undefined, RemoveCoApplicantDialogResult>(
			RemoveCoApplicantDialogComponent,
			{
				width: '480px',
				maxWidth: '90vw',
				disableClose: true,
			}
		);

		this.parentSub.add(dialogRef.afterClosed().subscribe((result) => {
			if (result?.reason) {
				// Removal logic
				this.removeCoApplicant(result.reason);
			}
		}));
	}

	private removeCoApplicant(reason: string) {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Removing co-applicant' },
		});

		// Taken before the removal clears it; the last name lives in Guarantor_Name
		const coApplicantName = [this.application.Co_Applicant_Name, this.application.Guarantor_Name]
			.filter((name) => name?.trim())
			.join(' ');

		// TODO: TransUnion to History, checklist and underwriting
		this.parentSub.add(this.applicationService
			.removeCoApplicant(this.application.id, reason, this.getOverrideType())
			.pipe(switchMap(() => this.applicationService.getApplication(this.application.id)))
			.subscribe({
				next: (latestApplication) => {
					this.saveCoApplicantNote('Co-applicant removed', coApplicantName, reason);
					this._bottomSheet.dismiss();
					this._snackBar.open(
						'Co-applicant removed',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					// The summary takes the latest application and passes it back in, which resets the form
					this.applicationChange.emit(latestApplication);
				},
				error: (error) => {
					console.error(error);
					this._bottomSheet.dismiss();
					this._snackBar.open(
						error?.error?.message && typeof error.error.message === 'string'
							? error.error.message
							: 'Co-applicant removal failed',
						SnackbarConstants.ACTION_DISMISS
					);
				},
			}));
	}

	// ---------------------------------------------------------------------------
	// Co-applicant history
	// ---------------------------------------------------------------------------

	// The dialog loads the history itself, so it is current when opened
	private handleCoApplicantHistoryClick() {
		this.dialog.open<CoApplicantHistoryDialogComponent, CoApplicantHistoryDialogData>(
			CoApplicantHistoryDialogComponent,
			{
				width: '640px',
				maxWidth: '90vw',
				data: { applicationId: this.application.id },
			}
		);
	}

	// ---------------------------------------------------------------------------
	// Restore co-applicant
	// ---------------------------------------------------------------------------

	private handleRestoreCoApplicantClick() {
		const dialogRef = this.dialog.open<
			RestoreCoApplicantDialogComponent,
			RestoreCoApplicantDialogData,
			RestoreCoApplicantDialogResult
		>(RestoreCoApplicantDialogComponent, {
			width: '560px',
			maxWidth: '90vw',
			disableClose: true,
			data: { removals: this.restorableCoApplicants },
		});

		this.parentSub.add(dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.restoreCoApplicant(result.removal, result.reason);
			}
		}));
	}

	private restoreCoApplicant(removal: RemovedCoApplicant, reason: string) {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Restoring co-applicant' },
		});

		this.parentSub.add(this.applicationService
			.restoreCoApplicant(this.application.id, removal.id, reason, this.getOverrideType())
			.pipe(switchMap(() => this.applicationService.getApplication(this.application.id)))
			.subscribe({
				next: (latestApplication) => {
					this.saveCoApplicantNote('Co-applicant restored', removal.coApplicantName, reason);
					this._bottomSheet.dismiss();
					this._snackBar.open(
						'Co-applicant restored',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					// The summary passes the latest application back in, which resets the form and reloads the removal state
					this.applicationChange.emit(latestApplication);
				},
				error: (error) => {
					console.error(error);
					this._bottomSheet.dismiss();
					this._snackBar.open(
						typeof error?.error?.message === 'string' ? error.error.message : 'Co-applicant restore failed',
						SnackbarConstants.ACTION_DISMISS
					);
				},
			}));
	}

	// Override_Type stamp ("name (timestamp)") written to Zoho with every save, removal and restore
	private getOverrideType(): string {
		return `${this.currentUser?.full_name} (${dayjs().format('YYYY-MM-DD HH:mm:ss')})`;
	}

	// Records the removal/restore in zoho_app_notes. Runs silently so it does not replace the result snackbar
	// The note reads "<co-applicant name> - <reason>"
	private saveCoApplicantNote(subject: string, coApplicantName: string, reason: string): void {
		this.parentSub.add(this.applicationService
			.createApplicationNote(this.application.id, {
				subject,
				note: coApplicantName ? `${coApplicantName} - ${reason}` : reason,
				communication: 'INT',
				dealName: this.application.Deal_Name,
				currentUser: this.currentUser,
			})
			.subscribe({
				error: (error) => console.error(error, `Failed to save "${subject}" note`),
			}));
	}
}
